import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableScene from "../core/ResizableScene";
import { PointsIndicator } from "../game/PointsIndicator";
import PlayerData from "../PlayerData";
import { SaveData } from "../SaveData";
import { LevelData } from "../scenes/LevelSelectScene";
import ARIA, { ARIAElement } from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import GameColors from "../utils/GameColors";
import TweenUtils from "../utils/TweenUtils";

export class LevelSelectButton extends Phaser.GameObjects.Container {
	lock: Phaser.GameObjects.Image;
	lockContainer: Phaser.GameObjects.Container;
	scene: ResizableScene;
	sparklesContainer: Phaser.GameObjects.Container;
	sparkles: PointsIndicator;
	saveData: SaveData;
	levelData: LevelData;
	button: Button;
	private unlockMeterBacker?: Phaser.GameObjects.Image;
	private scoreRequirmentARIA?: ARIAElement;
	private unlocked = false;

	constructor(scene: ResizableScene, levelData: LevelData, onClick: () => void, levelNum: number, bounce: boolean) {
		super(scene);
		this.scene = scene;
		this.levelData = levelData;
		this.sparkles = null;
		const button = (this.button = scene.add.button(0, 0, "lvlBacker", null, onClick, null));
		if (bounce == true) button.addPointerDownAnim(TweenUtils.BounceContainer(scene, this));
		this.add(button);
		this.setSize(button.width, button.height);

		const isUnlocked = (this.unlocked = PlayerData.Instance.saveData.unlockedLevels[levelData.name]);

		const icon = scene.add.image(0, 0, levelData.name + "Icon");
		this.add(icon);
		scene.alignIn.Center(icon, button, 0, -button.height * 0.25 - 25);

		let highScore = PlayerData.Instance.saveData.highScores[levelData.name];
		if (highScore == null || highScore == undefined) highScore = 0;

		if (highScore > 0) {
			const medal = scene.add.image(0, 0, "lvlMedal");
			this.add(medal);
			scene.alignIn.Center(medal, button, -12, medal.height / 2 - 24);
		}

		const nameGroup = scene.add.container(0, 0);
		this.add(nameGroup);

		const nameBG = scene.add.image(0, 0, "lvlNameBG");
		nameGroup.setSize(nameBG.width, nameBG.height);
		const numberBG = scene.add.image(0, 0, "lvlNumBG");
		scene.alignIn.LeftCenter(numberBG, nameBG, -30, 0);

		const lvlNumber = scene.add.text(0, 0, levelNum.toFixed(0), {
			fontSize: "70px",
			color: GameColors.PALE_PURPLE,
			fontFamily: Fonts.MontserratBold,
			align: "center",
		});
		lvlNumber.setOrigin(0.5);
		scene.alignIn.Center(lvlNumber, numberBG);

		const name = scene.add.text(0, 0, Strings.get(levelData.name), {
			fontSize: "36px",
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratBold,
			align: "left",
		});
		name.setOrigin(0, 0.5);
		name.setWordWrapWidth(nameBG.width - numberBG.width - 60 - 20);
		this.add(name);
		scene.alignTo.RightCenter(name, numberBG, 20, 0);

		nameGroup.add([nameBG, numberBG, lvlNumber, name]);
		scene.alignIn.Center(nameGroup, button, 0, -25);

		this.sparklesContainer = scene.add.container(0, 0);
		this.add(this.sparklesContainer);

		/* Locked container and assets*/

		if (levelData.name != "GreenCircle" && isUnlocked == false) {
			button.setTexture("lvlBackerLocked");
			this.lockContainer = scene.add.container(0, 0);
			this.add(this.lockContainer);
			scene.alignIn.Center(this.lockContainer, button);
			const lock = (this.lock = scene.add.image(0, 0, "lock"));
			this.lockContainer.add(lock);
			scene.alignIn.Center(lock, button, 0, button.height * 0.25 + 75);
			button.disableInteractive();

			const unlockMeterBacker = (this.unlockMeterBacker = scene.add.image(0, 0, "unlockMeterBacker"));
			this.lockContainer.add(unlockMeterBacker);
			scene.alignIn.Center(unlockMeterBacker, button, 0, 85);

			const unlockmeter = scene.add.image(0, 0, "unlockMeterEmpty");
			this.lockContainer.add(unlockmeter);

			scene.alignIn.Center(unlockmeter, unlockMeterBacker);

			let progress = PlayerData.Instance.saveData.totalScore / levelData.scoreRequirement;
			const meterFill = scene.add.image(0, 0, "unlockMeterFull");
			this.lockContainer.add(meterFill);
			meterFill.scaleX = 0;
			meterFill.setOrigin(0, 0.5);
			if (progress >= 1) {
				progress = 1;
				this.unlocked = true;
			}
			this.fillMeter(meterFill, progress);
			//meterFill.scaleX = progress;
			scene.alignIn.LeftCenter(meterFill, unlockmeter);
			const scoreReqText = scene.add.text(0, 0, levelData.scoreRequirement.toFixed(), {
				color: GameColors.WHITE,
				fontFamily: Fonts.MontserratBold,
				fontSize: "28px",
			});
			this.lockContainer.add(scoreReqText);
			scene.alignIn.Center(scoreReqText, unlockmeter);

			const meterStars = scene.add.image(0, 0, "unlockMeterStars");
			this.lockContainer.add(meterStars);

			scene.alignIn.LeftCenter(meterStars, unlockMeterBacker, 25, -3);
		}
	}

	setARIA(layer?: string): void {
		const name = `${Strings.get(this.levelData.name)} Level${this.unlocked ? "" : " Locked"}`;
		this.button.setARIA(name, layer);
		if (!this.unlocked && this.lockContainer) {
			const [score] = [this.levelData.scoreRequirement.toFixed()]; // , Strings.get(this.levelData.name)];
			const text = `You need a Total Score of ${score} to unlock this Level`;
			this.scoreRequirmentARIA = ARIA.addTextWithObject(this.unlockMeterBacker, text, layer);
		}
	}

	fillMeter(meterFill: Phaser.GameObjects.Image, progress: number): void {
		this.scene.tweens.add({
			targets: meterFill,
			scaleX: { from: 0, to: progress },
			duration: 800,
			onComplete: () => {
				if (progress >= 1) {
					this.playUnlockTween();
					PlayerData.Instance.saveData.unlockedLevels[this.levelData.name] = true;
					PlayerData.Instance.save();
				}
			},
		});
	}

	playUnlockTween(): void {
		const lockTimeline = this.scene.tweens.createTimeline();
		lockTimeline.add({
			targets: this.lock,
			x: { from: this.lock.x - 5, to: this.lock.x + 5 },
			duration: 125,
			yoyo: true,
			repeat: 1,
			ease: Phaser.Math.Easing.Quadratic.InOut,
			onComplete: () => {
				this.sparkles = new PointsIndicator(this.scene, 0, 0);
				this.sparklesContainer.add(this.sparkles);
				this.scene.alignIn.Center(this.sparkles, this.lock, 0, 15);
			},
		});

		lockTimeline.add({
			targets: this.lock,
			scale: { from: 1, to: 1.15 },
			duration: 200,
			onComplete: () => {
				this.button.setTexture("lvlBacker");
				this.lock.setTexture("lockOpen");
				this.button.setInteractive();
			},
		});
		lockTimeline.add({
			targets: this.lock,
			scale: { from: 1.15, to: 1 },
			duration: 200,
		});
		lockTimeline.add({
			targets: [this.lock, this.lockContainer],
			alpha: 0,
			duration: 750,
			ease: Phaser.Math.Easing.Back.Out,
			onComplete: () => {
				if (this.scoreRequirmentARIA) ARIA.removeElement(this.scoreRequirmentARIA);
			},
		});
		lockTimeline.play();
	}
}
